import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { CoreService } from '../../core/services/core.service';
import { GateView } from './gate-view';

interface GateCopy {
  heading: string;
  message: string;
}

const FALLBACK: Record<'maintenance' | 'prelaunch', GateCopy> = {
  maintenance: {
    heading: "We're Improving the Partner Workspace for You!",
    message:
      'The partner workspace is currently undergoing scheduled maintenance. We will be back shortly with a faster, more secure experience.',
  },
  prelaunch: {
    heading: 'The Partner Workspace is launching soon.',
    message:
      'Newspapers and accredited agencies will soon be able to manage publication requests, verify submissions and track settlements -- all from one place.',
  },
};

/**
 * Gate shown in place of the router-outlet while the partner workspace is
 * offline (see App). Reads state that already lives on CoreService.appConfig
 * -- fetched once at bootstrap -- rather than issuing a second request, and
 * derives which of the two admin-selected gate views (prelaunch or plain
 * maintenance) to render along with its copy, countdown and contact details.
 */
@Component({
  selector: 'app-maintenance',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GateView],
  template: `
    <app-gate-view
      [mode]="mode()"
      [heading]="copy().heading"
      [message]="copy().message"
      [endsAt]="showCountdown() ? endsAt() : null"
      [consentText]="consentText()"
      [contact]="contact()"
      [socialLinks]="socialLinks()"
      [checking]="checking()"
      (expired)="recheck()"
      (recheck)="recheck()"
    />
  `,
})
export class Maintenance {
  private readonly coreService = inject(CoreService);

  readonly checking = signal(false);

  constructor() {
    // The header normally restores this, but it lives inside the
    // router-outlet this page replaces -- so a visitor with a saved dark
    // preference would otherwise be forced into light mode until the site
    // reopens.
    if (localStorage.getItem('npr-theme') === 'dark') {
      document.documentElement.classList.add('dark');
    }
  }

  readonly mode = computed<'maintenance' | 'prelaunch'>(() => {
    const raw = this.coreService.appConfig()?.gate_mode?.partner;
    return raw === 'prelaunch' || raw === 'waitlist' ? 'prelaunch' : 'maintenance';
  });

  readonly display = computed(() => this.coreService.appConfig()?.gate_display?.partner);

  readonly copy = computed<GateCopy>(() => {
    const fallback = FALLBACK[this.mode()];
    const heading = this.display()?.heading?.trim();
    const message = this.display()?.message?.trim();
    return {
      heading: heading || fallback.heading,
      message: message || fallback.message,
    };
  });

  readonly showCountdown = computed(() => !!this.display()?.countdown_enabled && !!this.display()?.ends_at);
  readonly endsAt = computed(() => this.display()?.ends_at ?? null);

  readonly consentText = computed(() => this.coreService.appConfig()?.waitlist?.consent_text ?? '');
  readonly contact = computed(() => this.coreService.appConfig()?.support_contact ?? {});
  readonly socialLinks = computed(() => this.coreService.appConfig()?.social_links ?? {});

  recheck(): void {
    if (this.checking()) {
      return;
    }
    this.checking.set(true);
    this.coreService.fetchAppConfig().subscribe({
      complete: () => this.checking.set(false),
      error: () => this.checking.set(false),
    });
  }
}
